import {
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { savingProgressLabel } from '@/features/config/pages/StationTableView/saving-progress-label'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CancelIcon from '@mui/icons-material/Cancel'
import DeleteIcon from '@mui/icons-material/Delete'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import { debounce } from 'lodash'
import { motion } from 'framer-motion'
import { Fragment, useEffect, useMemo, useState, useRef } from 'react'

import StatusInterface from '@/components/common/StatusInterface'
import ButtonField from '@/components/form-control/ButtonField'
import SearchInput from '@/components/common/SearchInput'
import { DatabaseEnterIcon, LocalPinIcon } from '@/components/icons'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'
import { flyInVariantsDashboard } from '@/constant/common'
import useSearchInterfaceId from '@/features/admin/hooks/interface/useSearchInterfaceId'
import { useAuthContext } from '@/features/auth/context'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { SearchParams } from '@/types/common'
import { useSelector } from 'react-redux'
import { ConfigLayout } from '../../components/common'
import ConfigHeader from '../../components/common/Header'
import { useConfigUI } from '../../context'
import { useSearchStation } from '../../hooks/station'
import classes from './style.module.scss'
import PinStationsOnMapModal from '@features/config/components/modal/PinStationsOnMap/PinStationsOnMapModal'
import { useAppDispatch, useAppSelector } from '@store/stores'
import {
  fetchAllFilterData,
  stationGeoTagDeviceState,
  stationGeoTagDeviceSliceActions,
} from '@store/slices/StataionGeoTagDevice/stationGeoTagDeviceSlice'
import StationTableView, { StationTableViewRef } from '../StationTableView'
import { SavedViewsProvider } from '@/features/config/contexts/saved-views-context'
import SavedViewsTabs from '@/components/common/SavedViewsTabs'
import stationDataConfigApi from '@/api/config/stationDataConfig'
import { NotifyService } from '@/helpers'
import {
  useGuidedEdit,
  useGuidedIncomplete,
} from '@/features/config/contexts/guided-edit-context'

type Props = {
  interfaceId?: number | string
  embedded?: boolean
  /** SCRUM-1032 — the step has the whole viewport; the wizard chrome is hidden. */
  focusMode?: boolean
  /** Toggles focus mode. Absent outside the guided setup, where the button is not shown. */
  onToggleFocus?: () => void
}

/**
 * "N still to fill in · show" — the rows missing something the save needs.
 *
 * Shared by the guided step and /config. On /config the list pages, so the
 * count is unknown until the filter has pulled the whole list in; the chip then
 * offers the check rather than a number.
 */
const ToFillInChip = ({
  count,
  known,
  active,
  onToggle,
  t,
}: {
  count: number
  known: boolean
  active: boolean
  onToggle: () => void
  t: (key: string, params?: Record<string, unknown>) => string
}) => (
  <Box
    component="button"
    type="button"
    data-testid="stations-incomplete-filter"
    onClick={onToggle}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      height: 26,
      px: '10px',
      borderRadius: '13px',
      border: '1px solid',
      borderColor: active ? '#C77700' : 'rgba(199,119,0,0.4)',
      backgroundColor: active ? 'rgba(199,119,0,0.12)' : 'transparent',
      color: '#C77700',
      fontFamily: 'inherit',
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    }}
  >
    {known
      ? t('stationsProgressToFillIn', { count })
      : t('stationsProgressCheckToFillIn')}
    {known && (
      <Box component="span" sx={{ fontWeight: 400 }}>
        ·{' '}
        {active
          ? t('stationsProgressShowingOnlyToFillIn')
          : t('stationsProgressShowToFillIn')}
      </Box>
    )}
  </Box>
)

const Stations = ({
  interfaceId: propInterfaceId,
  embedded = false,
  focusMode = false,
  onToggleFocus,
}: Props) => {
  const MotionDiv = motion.div
  const { openSidebar, setSidebarView, openModal, setModalView } = useConfigUI()
  const { interfaceId: authInterfaceId, statusInterfaceId } = useAuthContext()
  const interfaceId =
    propInterfaceId != null ? Number(propInterfaceId) : authInterfaceId
  const { data: statusInterface } = useSearchInterfaceId(interfaceId + '')
  const { isViewOnly } = usePermission()
  const { t } = useTranslation()

  const themeMode = useSelector(selectTheme)
  const isDarkTheme = themeMode === mode_type.dark
  // Shared ghost/outlined toolbar button styling (theme-aware).
  // Edit and Add Stations are a pair of equals at the end of the toolbar, so
  // they are given one width rather than each taking the width of its label.
  const ACTION_BTN_WIDTH = '120px'
  const ghostBtnStyle = {
    height: '38px',
    borderRadius: '8px',
    backgroundColor: isDarkTheme ? '#0d2e3c' : '#ffffff',
    color: isDarkTheme ? '#c9d1d9' : '#1f2937',
    border: `1px solid ${isDarkTheme ? '#244653' : '#dfe3e6'}`,
    fontWeight: 600,
  }
  const dispatch = useAppDispatch()
  const stationGeoTagDevice = useAppSelector(stationGeoTagDeviceState)

  const [searchParams, setSearchParams] = useState<SearchParams>({
    PageSize: 10,
    PageIndex: 1,
  })
  const { data, refetch, isFetching } = useSearchStation(
    searchParams,
    Number(interfaceId),
  )
  const [openPinStations, setOpenPinStations] = useState(false)
  const [bulkEditMode, setBulkEditMode] = useState(false)
  const [isSavingBulkEdit, setIsSavingBulkEdit] = useState(false)
  const [selectedStationIds, setSelectedStationIds] = useState<string[]>([])
  const [isDeletingBulk, setIsDeletingBulk] = useState(false)
  const stationTableViewRef = useRef<StationTableViewRef>(null)
  /**
   * The "still to fill in" filter on /config, where there is no wizard progress
   * strip to hang it from. Reported by the grid (SCRUM-1032, 22 Sep).
   */
  const [toFillIn, setToFillIn] = useState<{
    count: number
    known: boolean
    only: boolean
    onToggle: () => void
  } | null>(null)
  // SCRUM-1032: the grid reports how far the autosave has got; the readout
  // lives on the toolbar row rather than on a line of its own above the list.
  const [progress, setProgress] = useState<{
    saved: number
    pending: number
    /** Every station this controller should end up with. Fixed for the sitting. */
    total: number
    issues: number
    issuesOnly: boolean
    onToggleIssuesOnly: () => void
    /** Rows still missing something the save needs. */
    incomplete: number
    incompleteOnly: boolean
    onToggleIncompleteOnly: () => void
    hideSaved: boolean
    onToggleHideSaved: () => void
    saving: boolean
    inFlight?: number
    /** Seconds left at the rate the rows are going through, or null. */
    etaSeconds?: number | null
    failed: number
    error?: string | null
    refused?: string | null
    repurposed: number
    typeMismatch: number
    duplicated: number
  } | null>(null)
  /**
   * "about 3m" / "about 40s" — the wait, at the rate the rows are going
   * through. Each station is written to the controller one at a time, so on a
   * long list this is minutes, and a user who knows that can leave it running
   * instead of watching it.
   */
  const formatEta = (seconds: number) => {
    if (seconds < 45)
      return t('stationsProgressEtaSeconds', {
        seconds: Math.max(5, Math.round(seconds / 5) * 5),
      })
    return t('stationsProgressEtaMinutes', {
      minutes: Math.round(seconds / 60),
    })
  }

  /**
   * How much of the list is written, 0–100.
   *
   * Rounded down, and held below 100 while anything is still pending: a bar
   * that reads 100% with rows left to write is worse than one that reads 99%.
   */
  const savePercent = useMemo(() => {
    if (!progress) return 0
    const total = progress.total || progress.saved + progress.pending
    if (total <= 0) return 0
    if (progress.pending === 0) return 100
    return Math.min(99, Math.floor((progress.saved / total) * 100))
  }, [progress])

  /**
   * SCRUM-1032 QC round 3, item 7 — what the wizard runs when the user leaves
   * this step.
   *
   * It used to be the full bulk save, and because that refuses a batch holding
   * any incomplete row — the ordinary state of a list built from a thousand
   * decoders — the wizard skipped it altogether on the way out of the station
   * step. Sub Area edited on three rows and then a click on another step in the
   * rail left those edits unwritten, saved only if the pause autosave happened
   * to be mid-flight (Wei Woon, 21 Sep).
   *
   * The quiet flush writes the rows that are ready and leaves the rest staged,
   * so leaving is safe without complaining about work that is not finished.
   * Finish is still held by `useGuidedIncomplete` below, which is where
   * "rows still to fill in" belongs.
   */
  // No flush time limit: on a hub this legitimately runs for minutes, and
  // every Station request has its own client timeout and bounded passes.
  const bulkEditByDefault = useGuidedEdit(
    'stations',
    () => stationTableViewRef.current?.flushReadyRows(),
    { timeoutMs: null },
  )

  useEffect(() => {
    if (bulkEditByDefault) setBulkEditMode(true)
  }, [bulkEditByDefault])

  const handleEditStation = (id: string) => {
    // ViewOnly users cannot edit stations
    if (isViewOnly) return

    openSidebar()
    setSidebarView({ type: 'STATIONS_VIEW', id })
  }

  const handleAddNewStation = () => {
    // ViewOnly users cannot add stations
    if (isViewOnly) return

    openSidebar()
    setSidebarView({ type: 'STATIONS_VIEW' })
  }

  // SCRUM-1032: the wizard holds Finish while rows are still to be filled in.
  // Finish is the end of the setup, and a station list left half entered is
  // the thing this step exists to prevent.
  const pendingStations = bulkEditByDefault ? (progress?.pending ?? 0) : 0
  useGuidedIncomplete(
    'stations',
    pendingStations > 0
      ? pendingStations === 1
        ? t('stationsBlockedPendingOne')
        : t('stationsBlockedPendingMany', { count: pendingStations })
      : null,
  )

  const handleDeleteStation = (id: string, name?: string) => {
    // ViewOnly users cannot delete stations
    if (isViewOnly) return

    // SCRUM-1062: the confirmation names the station being removed. The caller
    // may pass it; otherwise it is read off the loaded row, falling back to the
    // decoder address so there is something to check against.
    const targetStation = data?.list?.find((s: any) => s.id === id)
    const stationName =
      name ||
      (targetStation?.controller
        ? `${targetStation.controller} (${targetStation.address}-${targetStation.index})`
        : targetStation?.controller || undefined)

    openModal()
    setModalView({
      type: 'DELETE_STATION_VIEW',
      id,
      interfaceId: interfaceId + '',
      onSuccess: () => stationTableViewRef.current?.removeRows([id]),
      name: stationName,
    })
  }

  const handleViewStation = (id: string) => {
    openSidebar()
    setSidebarView({
      type: 'STATIONS_OVERVIEW',
      id,
    })
  }

  const onOpenPinStations = () => {
    // ViewOnly users cannot geocode stations
    if (isViewOnly) return

    setOpenPinStations(true)
  }

  const onClosePinStations = (refresh?: boolean) => {
    setOpenPinStations(false)
    if (refresh) refetch()
  }

  // SCRUM-1032: the station list was the only list in the app with no search.
  // The grid it renders already accepts a `keyword` and passes it to the
  // station query — nothing was ever wired to produce one.
  const [keyword, setKeyword] = useState('')
  const handleSearch = useMemo(
    () =>
      debounce((event: any) => {
        setKeyword(String(event.target.value ?? '').trim())
      }, 500),
    [],
  )

  const handleToggleBulkEdit = () => {
    // ViewOnly users cannot bulk edit stations
    if (isViewOnly) return

    setBulkEditMode(!bulkEditMode)
  }

  const handleSaveBulkEdit = async () => {
    if (!stationTableViewRef.current) return

    setIsSavingBulkEdit(true)
    try {
      await stationTableViewRef.current.handleSave()
    } finally {
      setIsSavingBulkEdit(false)
    }
  }

  const handleCancelBulkEdit = () => {
    setBulkEditMode(false)
    setSelectedStationIds([])
  }

  const handleBulkDelete = async () => {
    if (selectedStationIds.length === 0 || !interfaceId) return
    const count = selectedStationIds.length
    if (!window.confirm(t('stationsConfirmBulkDelete', { count })))
      return

    setIsDeletingBulk(true)
    try {
      await Promise.all(
        selectedStationIds.map((id) =>
          stationDataConfigApi.delete(String(interfaceId), id),
        ),
      )
      NotifyService.success(t('stationsToastDeleteSuccess', { count }))
      // Remove rows from grid immediately
      stationTableViewRef.current?.removeRows(selectedStationIds)
      setSelectedStationIds([])
    } catch (error: any) {
      NotifyService.error(error?.message || t('stationsToastDeleteFailed'))
    } finally {
      setIsDeletingBulk(false)
    }
  }

  const [key, setKey] = useState(0)

  // useEffect(() => {
  //   const intervalId = setInterval(() => {
  //     setKey((prevKey) => prevKey + 1)
  //   }, 30000) // Mỗi 30 giây

  //   return () => clearInterval(intervalId) // Xóa bỏ interval khi component bị unmounted
  // }, [])

  useEffect(() => {
    refetch()
  }, [key, refetch])

  // Real-time station status updates are handled inside StationTableView via
  // SignalR (ActionSignalR.STATION_UPDATE).

  useEffect(() => {
    dispatch(stationGeoTagDeviceSliceActions.reset())
    return () => {
      dispatch(stationGeoTagDeviceSliceActions.reset())
    }
  }, [])

  useEffect(() => {
    if (interfaceId) {
      const search = { ...searchParams }
      search.Keyword = stationGeoTagDevice.filter.Keyword
      search.subAreaIds = stationGeoTagDevice.filter.SubAreaIds
      search.plantIds = stationGeoTagDevice.filter.PlantIds
      search.soilIds = stationGeoTagDevice.filter.SoilIds
      search.emitterIds = stationGeoTagDevice.filter.EmitterIds
      setSearchParams(search)
    }
  }, [interfaceId, stationGeoTagDevice.filter])

  useEffect(() => {
    if (interfaceId) {
      dispatch(fetchAllFilterData(interfaceId))
    }
  }, [interfaceId])

  const content = (
    <Stack
          className={`${classes.wrapper} ${
            statusInterface &&
            statusInterface.state === 'Offline' &&
            statusInterfaceId &&
            'wrapper-disabled'
          }`}
        >
          {/* SCRUM-1032: the wizard supplies its own chrome above this, so the
              toolbar starts at the top of the step rather than under a band of
              margin meant for the /config page header. */}
          <Stack mb={embedded ? '8px' : '12px'} mt={embedded ? 0 : '5px'}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ gap: 2, minWidth: 0, width: '100%' }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ flex: 1, minWidth: 0, overflow: 'visible', py: '4px' }}
              >
                {/* /config only. In the wizard the rows are drafted from the
                    decoders that have no station yet, and "has no station" is
                    decided from this very list — a keyword narrows it
                    server-side, so every saved station that did not match would
                    look unstationed and get a duplicate draft. */}
                {!bulkEditByDefault && (
                  <SearchInput
                    sx={{
                      ...ghostBtnStyle,
                      width: 220,
                      boxShadow: 'none',
                    }}
                    name={t('stationsSearchPlaceholder')}
                    onChange={handleSearch}
                  />
                )}
                {/* Columns and the saved views are one feature — the button
                    is where a view is created, renamed and deleted, and the
                    strip is where one is picked. They are grouped tightly
                    here, and spaced from the rest of the toolbar, so that
                    reads. Both are hidden in the wizard: it ships a fixed
                    column set and answers width with Expand instead. */}
                {!bulkEditByDefault && (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                  >
                    <ButtonField
                      startIcon={<ViewColumnIcon />}
                      style={{ ...ghostBtnStyle, minWidth: '80px' }}
                      name={t('commonActionsColumns')}
                      onClick={(e: any) => {
                        if (stationTableViewRef.current) {
                          stationTableViewRef.current.handleOpenColumnVisibility(
                            e,
                          )
                        }
                      }}
                    />
                    <SavedViewsTabs
                      onApplyColumns={(cols) =>
                        stationTableViewRef.current?.applyColumns(cols)
                      }
                    />
                    {/* Stations missing something the save needs. On /config
                        the list pages, so the count arrives with the whole
                        list the filter pulls in. */}
                    {toFillIn && (toFillIn.count > 0 || !toFillIn.known) && (
                      <ToFillInChip
                        count={toFillIn.count}
                        known={toFillIn.known}
                        active={toFillIn.only}
                        onToggle={toFillIn.onToggle}
                        t={t}
                      />
                    )}
                  </Stack>
                )}

                {/* How far the autosave has got, on the toolbar row. The
                    spacer pushes it and Expand to opposite ends. */}
                {progress && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ minWidth: 0, flexWrap: 'wrap' }}
                  >
                    {/* How much of the list is written, as a proportion.
                        "315 of 500" is a pair of numbers to divide in your
                        head; a bar and a percentage are the same fact already
                        answered, and on a list that takes minutes to write
                        that is the difference between waiting and wondering. */}
                    {(progress.total || progress.saved + progress.pending) > 0 && (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ flexShrink: 0 }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            width: 132,
                            height: 6,
                            borderRadius: 3,
                            overflow: 'hidden',
                            backgroundColor: 'border.main',
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              width: `${savePercent}%`,
                              borderRadius: 3,
                              backgroundColor: '#0A6B62',
                              transition: 'width .3s ease',
                            }}
                          />
                          {/* A pass writes one row at a time, so the bar can
                              sit still for a second between rows. The sheen
                              says the save is alive while it does. */}
                          {progress.saving && (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                '@keyframes stationSaveSheen': {
                                  '0%': { transform: 'translateX(-100%)' },
                                  '100%': { transform: 'translateX(320%)' },
                                },
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  inset: 0,
                                  width: '32%',
                                  background:
                                    'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
                                  animation:
                                    'stationSaveSheen 1.2s ease-in-out infinite',
                                },
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'text.main',
                            minWidth: 34,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {savePercent}%
                        </Typography>
                      </Stack>
                    )}
                    {progress.saving && <CircularProgress size={12} />}
                    {progress.saving ? (
                      <Typography
                        sx={{ fontSize: '13px', color: 'grey.800', whiteSpace: 'nowrap' }}
                      >
                        {(() => {
                          const label = savingProgressLabel(progress)
                          return t(label.key, {
                            ...label.values,
                            ...(label.values.eta != null
                              ? { eta: formatEta(Number(label.values.eta)) }
                              : {}),
                          })
                        })()}
                      </Typography>
                    ) : (
                      /* The count is the control: the saved rows are the ones
                         the user has finished with, and scrolling past them to
                         find where they got to is most of the work of coming
                         back to this step. One click puts them away. */
                      <Box
                        component="button"
                        type="button"
                        onClick={progress.onToggleHideSaved}
                        disabled={progress.saved === 0}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          height: 26,
                          px: '10px',
                          borderRadius: '13px',
                          border: '1px solid',
                          borderColor: progress.hideSaved
                            ? 'primary.main'
                            : 'border.main',
                          backgroundColor: progress.hideSaved
                            ? (theme) =>
                                theme.palette.mode === 'dark'
                                  ? 'rgba(255,255,255,0.08)'
                                  : 'rgba(0,71,68,0.08)'
                            : 'transparent',
                          color: progress.hideSaved ? 'primary.main' : 'text.main',
                          fontFamily: 'inherit',
                          fontSize: '13px',
                          cursor: progress.saved === 0 ? 'default' : 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {progress.saved}
                        </Box>
                        <Box component="span" sx={{ color: 'grey.800' }}>
                          {t('stationsProgressSavedOfTotal', {
                            total:
                              progress.total ||
                              progress.saved + progress.pending,
                          })}
                        </Box>
                        {progress.saved > 0 && (
                          <Box
                            component="span"
                            sx={{
                              color: progress.hideSaved ? 'primary.main' : 'grey.800',
                              fontWeight: progress.hideSaved ? 600 : 400,
                            }}
                          >
                            ·{' '}
                            {progress.hideSaved
                              ? t('stationsProgressHidden')
                              : t('stationsProgressHide')}
                          </Box>
                        )}
                      </Box>
                    )}
                    {/* What is left to do, in a list of a thousand: the rows
                        still missing something the save needs. */}
                    {progress.incomplete > 0 && (
                      <ToFillInChip
                        count={progress.incomplete}
                        known
                        active={progress.incompleteOnly}
                        onToggle={progress.onToggleIncompleteOnly}
                        t={t}
                      />
                    )}

                    {/* The way to find the problems in a list of a thousand.
                        An error names a count; this narrows to the rows. */}
                    {progress.issues > 0 && (
                      <Box
                        component="button"
                        type="button"
                        onClick={progress.onToggleIssuesOnly}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          height: 26,
                          px: '10px',
                          borderRadius: '13px',
                          border: '1px solid',
                          borderColor: progress.issuesOnly
                            ? '#dc3545'
                            : 'rgba(220,53,69,0.4)',
                          backgroundColor: progress.issuesOnly
                            ? 'rgba(220,53,69,0.12)'
                            : 'transparent',
                          color: '#dc3545',
                          fontFamily: 'inherit',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {t('stationsProgressIssuesToFix', {
                          count: progress.issues,
                        })}
                        <Box component="span" sx={{ fontWeight: 400 }}>
                          ·{' '}
                          {progress.issuesOnly
                            ? t('stationsProgressShowingOnlyIssues')
                            : t('stationsProgressShowIssues')}
                        </Box>
                      </Box>
                    )}

                    {progress.failed > 0 && (
                      <Typography
                        sx={{ fontSize: '13px', color: '#dc3545', minWidth: 0 }}
                      >
                        {t('stationsProgressFailed', { count: progress.failed })}
                        {/* What the server said, not just that it said no —
                            otherwise a stuck row is a mystery to everyone. */}
                        {progress.error ? `: ${progress.error}` : ''}
                      </Typography>
                    )}
                    {progress.refused && (
                      <Typography
                        data-testid="station-refused-edit"
                        sx={{ fontSize: '13px', color: '#dc3545', minWidth: 0 }}
                      >
                        {progress.refused}
                      </Typography>
                    )}
                    {progress.repurposed > 0 && (
                      <Typography sx={{ fontSize: '13px', color: '#dc3545' }}>
                        {progress.repurposed === 1
                          ? t('stationsWarningRepurposedOne')
                          : t('stationsWarningRepurposedMany', {
                              count: progress.repurposed,
                            })}
                      </Typography>
                    )}
                    {progress.duplicated > 0 && (
                      <Typography sx={{ fontSize: '13px', color: '#dc3545' }}>
                        {t('stationsWarningDuplicated', {
                          count: progress.duplicated,
                        })}
                      </Typography>
                    )}
                    {progress.typeMismatch > 0 && (
                      <Typography sx={{ fontSize: '13px', color: '#dc3545' }}>
                        {progress.typeMismatch === 1
                          ? t('stationsWarningTypeMismatchOne')
                          : t('stationsWarningTypeMismatchMany', {
                              count: progress.typeMismatch,
                            })}
                      </Typography>
                    )}
                    {/* The row tints, said in words. Colour alone is a code
                        the user has to crack — and on a list this long the
                        first question is always "which of these have I
                        done?". */}
                    <Stack
                      direction="row"
                      sx={{ alignItems: 'center', gap: '12px', flexShrink: 0 }}
                    >
                      {/* The same tints the rows carry, which the grid takes
                          from its own dark palette — so these follow it. */}
                      {[
                        {
                          label: t('stationsLegendSaved'),
                          tint: isDarkTheme
                            ? 'rgba(102,240,160,0.14)'
                            : 'rgba(0,71,68,0.10)',
                          accent: isDarkTheme ? '#66f0a0' : '#0a6b62',
                        },
                        {
                          label: t('stationsLegendToFillIn'),
                          tint: isDarkTheme
                            ? 'rgba(245,158,11,0.2)'
                            : 'rgba(245,158,11,0.16)',
                          accent: isDarkTheme ? '#fdba2e' : '#F59E0B',
                        },
                        {
                          label: t('stationsLegendNeedsAttention'),
                          tint: isDarkTheme
                            ? 'rgba(253,162,155,0.18)'
                            : 'rgba(220,53,69,0.14)',
                          accent: isDarkTheme ? '#fda29b' : '#dc3545',
                        },
                      ].map((item) => (
                        <Stack
                          key={item.label}
                          direction="row"
                          sx={{ alignItems: 'center', gap: '6px' }}
                        >
                          <Box
                            sx={{
                              width: 14,
                              height: 14,
                              borderRadius: '3px',
                              backgroundColor: item.tint,
                              boxShadow: `inset 3px 0 0 0 ${item.accent}`,
                            }}
                          />
                          <Typography
                            sx={{ fontSize: '12px', color: 'grey.800' }}
                          >
                            {item.label}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                )}


                <Box sx={{ flexGrow: 1 }} />

                {/* Expand the list to the whole viewport, as the decoder step
                    does: a controller with hundreds of stations is a long
                    scroll through a ten-row window otherwise. */}
                {embedded && onToggleFocus && (
                  <ButtonField
                    startIcon={
                      focusMode ? <FullscreenExitIcon /> : <FullscreenIcon />
                    }
                    style={{ ...ghostBtnStyle, minWidth: '89px' }}
                    name={
                      focusMode
                        ? t('stationsExitExpand')
                        : t('commonLabelsExpand')
                    }
                    onClick={onToggleFocus}
                  />
                )}
                {bulkEditMode && selectedStationIds.length > 0 && (
                  <LoadingButton
                    startIcon={<DeleteIcon />}
                    loading={isDeletingBulk}
                    loadingPosition="start"
                    variant="contained"
                    onClick={handleBulkDelete}
                    sx={{
                      height: '38px',
                      minWidth: '70px',
                      borderRadius: '8px',
                      backgroundColor: '#EF4444',
                      color: '#fff',
                      fontWeight: 600,
                      flexShrink: 0,
                      '&:hover': { backgroundColor: '#DC2626' },
                    }}
                  >
                    {t('stationsDeleteSelected', {
                      count: selectedStationIds.length,
                    })}
                  </LoadingButton>
                )}
              </Stack>

              <MotionDiv
                className={classes.header}
                initial="initialLeft"
                variants={flyInVariantsDashboard}
                animate="animate2"
                style={{ flexShrink: 0 }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  {/* SCRUM-1032: pinning stations on a map is a job for after
                      they exist. The wizard hands that to its own map step. */}
                  {!bulkEditByDefault && (
                  <Stack
                    direction="row"
                    sx={{ position: 'relative', isolation: 'isolate' }}
                  >
                    <ButtonField
                      startIcon={<LocalPinIcon />}
                      style={{
                        ...ghostBtnStyle,
                        minWidth: '89px',
                        opacity: isViewOnly ? 0.6 : 1,
                      }}
                      name={t('stations.stationGeoCode')}
                      onClick={onOpenPinStations}
                      disabled={isViewOnly}
                    />
                    {data?.list && data.unpinnedStations > 0 && (
                      <Stack
                        sx={{
                          position: 'absolute',
                          top: '-6px',
                          right: '-6px',
                          zIndex: 1,
                          backgroundColor: '#FF4F5E',
                          borderRadius: '50%',
                          minWidth: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0px 0px 0px 4px rgba(255, 79, 94, 0.12)',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#FFFFFF',
                            fontSize: '12px',
                            fontWeight: 600,
                            lineHeight: 1,
                          }}
                        >
                          {data.unpinnedStations > 99
                            ? '99+'
                            : data.unpinnedStations}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                  )}
                  {/* SCRUM-1032: the wizard owns saving — the step writes each
                      row as it becomes complete and Finish flushes the rest —
                      so its own Cancel and Save are hidden, along with the Edit
                      toggle, since bulk edit is always on there. */}
                  {bulkEditByDefault ? null : !bulkEditMode ? (
                    <ButtonField
                      startIcon={<EditIcon />}
                      style={{
                        ...ghostBtnStyle,
                        minWidth: ACTION_BTN_WIDTH,
                        opacity: isViewOnly ? 0.6 : 1,
                      }}
                      name={t('commonActionsBulkEdit')}
                      onClick={handleToggleBulkEdit}
                      disabled={isViewOnly}
                    />
                  ) : (
                    <>
                      <ButtonField
                        startIcon={<CancelIcon />}
                        style={{
                          height: '38px',
                          minWidth: '70px',
                          borderRadius: '8px',
                          // A pale pink pill reads as a hole punched in the
                          // dark theme; tint the dark ground instead.
                          backgroundColor: isDarkTheme
                            ? 'rgba(253,162,155,0.12)'
                            : '#fff2f2',
                          color: isDarkTheme ? '#fda29b' : '#dc3545',
                          border: `1px solid ${
                            isDarkTheme ? 'rgba(253,162,155,0.4)' : '#f3c2c5'
                          }`,
                          fontWeight: 600,
                        }}
                        name={t('commonActionsCancel')}
                        onClick={handleCancelBulkEdit}
                        disabled={isSavingBulkEdit}
                      />
                      <LoadingButton
                        startIcon={<SaveIcon />}
                        loading={isSavingBulkEdit}
                        loadingPosition="start"
                        variant="contained"
                        style={{
                          height: '38px',
                          minWidth: '70px',
                          borderRadius: '8px',
                        }}
                        sx={{
                          backgroundColor: '#004744',
                          color: '#fff',
                          fontWeight: 600,
                          '&:hover': { backgroundColor: '#002031' },
                        }}
                        // Don't steal focus from an open cell editor on mousedown:
                        // otherwise the editor blurs and its typed-but-uncommitted
                        // value is discarded before handleSave() can flush it, so
                        // the stale value gets saved. Keeping focus lets
                        // handleSave -> stopEditing() commit the current input.
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleSaveBulkEdit}
                      >
                        {t('commonActionsSave')}
                      </LoadingButton>
                    </>
                  )}

                  {/* SCRUM-1032: the wizard's list is already every station
                      the decoders describe, and the drawer this opens asks for
                      the decoder by hand. A station with no decoder is not one
                      the step can make. */}
                  {!bulkEditByDefault && (
                    <ButtonField
                      startIcon={<DatabaseEnterIcon />}
                      style={{
                        height: '38px',
                        minWidth: ACTION_BTN_WIDTH,
                        borderRadius: '8px',
                        backgroundColor: '#004744',
                        color: '#ffffff',
                        fontWeight: 600,
                        opacity: isViewOnly ? 0.6 : 1,
                      }}
                      name={t('stations.list.addStation')}
                      onClick={handleAddNewStation}
                      disabled={isViewOnly}
                    />
                  )}
                </Stack>
              </MotionDiv>
            </Stack>
          </Stack>

          {/* minHeight 0 so this pane can be shorter than its content: without
              it the grid pushed the page taller and the browser scrolled
              instead of the list. */}
          <Stack sx={{ flexGrow: 1, minHeight: 0 }}>
            <StationTableView
              ref={stationTableViewRef}
              interfaceId={interfaceId ?? undefined}
              data={data?.list}
              totalCount={data?.totalCount}
              onEditItem={handleEditStation}
              onDeleteItem={handleDeleteStation}
              onViewItem={handleViewStation}
              isLoadingMore={isFetching && (searchParams.PageIndex ?? 1) > 1}
              bulkEditMode={bulkEditMode}
              onToggleBulkEdit={handleToggleBulkEdit}
              onSelectedRowsChanged={setSelectedStationIds}
              // SCRUM-1032 / SCRUM-1040: in the wizard the list starts from the
              // decoders the user marked as stations, so the step opens on rows
              // to fill in rather than an empty grid.
              draftFromDecoders={bulkEditByDefault}
              // Never while drafting — see the search input above.
              keyword={bulkEditByDefault ? undefined : keyword}
              onProgressChange={setProgress}
              onIncompleteFilterChange={setToFillIn}
            />
          </Stack>

          {openPinStations && (
            <Fragment>
              <PinStationsOnMapModal
                open={true}
                interfaceId={Number(interfaceId)}
                onClose={onClosePinStations}
                tabValue="0"
              />
            </Fragment>
          )}
        </Stack>
  )

  return (
    <SavedViewsProvider tableKey="station-table">
      {embedded ? (
        content
      ) : (
        <ConfigLayout
          title={t('menuDataConfigStations')}
          header={
            <ConfigHeader
              subtitle={t('dataConfig')}
              title={t('menuDataConfigStations')}
            />
          }
        >
          {content}
          <StatusInterface />
        </ConfigLayout>
      )}
    </SavedViewsProvider>
  )
}

export default Stations
